import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RotateCcw, Trash2, XCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { getApiHeaders } from '../../utils/apiHelpers';
import { API_ARCHIVIO, dataItaliana, mb } from './archivioApi';
import { ElencoCestino } from './CestinoFoto';

const erroreApi = (e) => e?.response?.data?.detail || 'Operazione non riuscita, riprova';

const ETICHETTE_LAVORO = {
  libera: 'Sposto le foto nel cestino…',
  ripristina: 'Ripristino le foto…',
  svuota: 'Elimino definitivamente le foto…',
};

const Conferma = ({ testo, etichetta, onSi, onNo, invio, id, pericolo = false }) => (
  <div className={`rounded-xl border p-3 space-y-2 ${pericolo ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-amber-50'}`} data-testid={`spazio-conferma-${id}`}>
    <p className={`text-sm ${pericolo ? 'text-red-900' : 'text-amber-900'}`}>{testo}</p>
    <div className="flex gap-2">
      <Button variant="outline" className="flex-1" onClick={onNo} disabled={invio} data-testid={`spazio-conferma-${id}-no`}>Annulla</Button>
      <Button className={`flex-1 ${pericolo ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'}`} onClick={onSi} disabled={invio} data-testid={`spazio-conferma-${id}-si`}>
        {invio && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}{etichetta}
      </Button>
    </div>
  </div>
);

export const ProgressoLavoro = ({ lavoro }) => {
  const perc = lavoro.totale ? Math.round((lavoro.fatte / lavoro.totale) * 100) : 0;
  return (
    <div className="rounded-xl border border-purple-200 bg-purple-50 p-3" data-testid="spazio-progresso">
      <p className="flex items-center gap-2 text-sm font-medium text-purple-800">
        <Loader2 className="h-4 w-4 animate-spin" />
        {ETICHETTE_LAVORO[lavoro.tipo] || ETICHETTE_LAVORO.libera} {lavoro.fatte}/{lavoro.totale}
      </p>
      <div className="mt-2 h-1.5 rounded-full bg-purple-100 overflow-hidden">
        <div className="h-full bg-purple-500 transition-[width] duration-500" style={{ width: `${perc}%` }} />
      </div>
    </div>
  );
};

const avvia = async (azione, setInvio, onAvviato) => {
  setInvio(true);
  try {
    const { data } = await axios.post(`${API_ARCHIVIO}/spazio/${azione}`, {}, { headers: getApiHeaders(), timeout: 120000 });
    if (data.avviato) onAvviato(data.totale);
    else toast.info('Non c\'è niente da fare');
  } catch (e) {
    toast.error(erroreApi(e));
  } finally {
    setInvio(false);
  }
};

const BottoniCestino = ({ c, chiedi, setChiedi, fai, invio }) => {
  if (chiedi === 'ripristina') {
    return <Conferma id="ripristina" invio={invio} etichetta="Sì, ripristina" onSi={() => fai('ripristina')} onNo={() => setChiedi(null)}
      testo={`Rimetto nel database le ${c.liberate} foto liberate con il pulsante "Libera spazio".`} />;
  }
  if (chiedi === 'svuota') {
    return <Conferma id="svuota" pericolo invio={invio} etichetta="Sì, elimina per sempre" onSi={() => fai('svuota')} onNo={() => setChiedi(null)}
      testo={`Elimino definitivamente ${c.eliminabili.n} foto (${mb(c.eliminabili.byte)}) che sono nel cestino da più di 30 giorni. Non si potranno più recuperare; le scansioni restano.`} />;
  }
  return (
    <div className="space-y-2">
      {c.liberate > 0 && (
        <Button variant="outline" className="w-full" onClick={() => setChiedi('ripristina')} data-testid="spazio-ripristina-btn">
          <RotateCcw className="h-4 w-4 mr-1.5" /> Ripristina tutte le foto liberate ({c.liberate})
        </Button>
      )}
      {c.eliminabili.n > 0 ? (
        <Button variant="outline" className="w-full border-red-200 text-red-700 hover:bg-red-50" onClick={() => setChiedi('svuota')} data-testid="spazio-svuota-btn">
          <XCircle className="h-4 w-4 mr-1.5" /> Elimina per sempre {c.eliminabili.n} foto oltre 30 giorni
        </Button>
      ) : (
        <p className="text-xs text-gray-500" data-testid="spazio-svuota-info">
          Nessuna foto è nel cestino da più di 30 giorni: la prima si potrà eliminare dal {dataItaliana(c.scade_at?.slice(0, 10))}.
        </p>
      )}
    </div>
  );
};

const Cestino = ({ c, onCambiato, ...bottoni }) => (
  <div className="min-w-0 rounded-xl border border-gray-200 p-3 space-y-2" data-testid="spazio-cestino">
    <p className="text-sm text-gray-700" data-testid="spazio-cestino-riepilogo">
      Nel cestino: {c.n} foto ({mb(c.byte)}). Restano recuperabili finché non le elimini tu.
    </p>
    <ElencoCestino n={c.n} onCambiato={onCambiato} />
    <BottoniCestino c={c} {...bottoni} />
  </div>
);

export const AzioniSpazio = ({ d, onAvviato, onCambiato }) => {
  const [chiedi, setChiedi] = useState(null);
  const [invio, setInvio] = useState(false);
  const fai = (azione) => avvia(azione, setInvio, (totale) => { setChiedi(null); onAvviato(azione, totale); });
  if (!d.puo_liberare) {
    return <p className="text-xs text-gray-500" data-testid="spazio-solo-responsabile">Solo il responsabile può liberare lo spazio.</p>;
  }
  const lib = d.liberabili_totale;
  return (
    <div className="min-w-0 space-y-3" data-testid="spazio-azioni">
      {lib.n > 0 && (chiedi === 'libera' ? (
        <Conferma id="libera" invio={invio} etichetta="Sì, libera spazio" onSi={() => fai('libera')} onNo={() => setChiedi(null)}
          testo={`Sposto ${lib.n} foto originali (${mb(lib.byte)}) nel cestino: escono subito dal database. Scansioni, etichette e lotti restano come sono.`} />
      ) : (
        <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={() => setChiedi('libera')} data-testid="spazio-libera-btn">
          <Trash2 className="h-4 w-4 mr-1.5" /> Libera {mb(lib.byte)}
        </Button>
      ))}
      {d.cestino.n > 0 && <Cestino c={d.cestino} onCambiato={onCambiato} chiedi={chiedi} setChiedi={setChiedi} fai={fai} invio={invio} />}
    </div>
  );
};
